import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { SessionService } from 'src/app/shared/services/session.service';
import { BaseComponent } from 'src/app/core/base.component';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { CreditCardFeeType } from 'src/app/shared/models/creditcard-fee-type.enum';
import { MatDialog } from '@angular/material/dialog';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { AccountService } from 'src/app/account/account.service';
import { MatTableDataSource } from '@angular/material/table';
import { OrganizationService } from 'src/app/shared/services/identity/organization.service';
import { OperatingHours } from '../models/operating-hours';
import { GtStripeService } from 'src/app/shared/services/gt-stripe.service';
import { StripeSearchOptions } from 'src/app/shared/models/stripe/stripe-search-options';
import { StripeConnectExpressCreateModalComponent } from 'src/app/shared/modals/stripe-connect-express-create-modal/stripe-connect-express-create-modal.component';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';

@Component({
    selector: 'simbook-my-account',
    templateUrl: './my-account.component.html',
    styleUrls: ['./my-account.component.scss'],
    standalone: false
})
export class MyAccountComponent extends BaseComponent implements OnInit {
  CreditCardFeeType = CreditCardFeeType;
  selectedTabIndex: number = 0;

  userForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl('', Validators.required),
    email: new FormControl('', [Validators.required, Validators.email]),
    phoneNumber: new FormControl('', Validators.required),
  });

  organizationForm = new FormGroup({
    organizationName: new FormControl('', Validators.required),
    widgetApiKey: new FormControl({ value: '', disabled: true }),
    creditCardFeeType: new FormControl(CreditCardFeeType.Fixed),
    creditCardFee: new FormControl(0, [Validators.required, Validators.min(0)]),
    additionalEmail: new FormControl('', [Validators.email]),
    timezone: new FormControl('America/New_York', Validators.required)
  });

  additionalEmails: string[] = [];
  dataSource: MatTableDataSource<string>;
  emailErrorMessage: string = '';
  operatingHoursErrorMessage: any = { day: '', message: '' };
  days = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  operatingHours: OperatingHours[] = [];
  timeOptions: { value: string; display: string }[] = [];
  stripeLoginLink: any;
  option: StripeSearchOptions = new StripeSearchOptions();

  // US Timezones - Generated dynamically
  usTimezones: { value: string; label: string }[] = [];

  constructor(sessionService: SessionService,
    stringHelpers: StringHelpers,
    private accountService: AccountService,
    private organizationService: OrganizationService,
    private dialog: MatDialog,
    private stripeService: GtStripeService,
    private router: Router) {
    super(sessionService, stringHelpers);
    this.generateTimeOptions();
  }

  ngOnInit(): void {
    this.loadUserData();
    this.dataSource = new MatTableDataSource<string>(this.additionalEmails);
    this.generateUsTimezones();
  }

  private loadUserData(): void {
    this.isLoading = true;
    this.accountService.getUserByAuth0Id(this.sessionService.getUser().auth0Id).subscribe({
      next: (response) => {
        const currentUser = this.sessionService.getUser();
        if (JSON.stringify(currentUser) !== JSON.stringify(response)) {
          this.sessionService.setUser(response);
        }
        
        // Patch user form
        this.userForm.patchValue({
          firstName: response.firstName,
          lastName: response.lastName,
          email: response.email,
          phoneNumber: response.phoneNumber
        });

        // Patch organization form
        this.organizationForm.patchValue({
          organizationName: response.organizationName,
          widgetApiKey: response.apiKey,
          creditCardFeeType: response.creditCardFeeType,
          creditCardFee: response.creditCardFee,
          timezone: response.timeZone || 'America/New_York'
        });

        this.additionalEmails = response.additionalEmails ? response.additionalEmails.split(',').map(email => email.trim()) : [];
        this.dataSource.data = this.additionalEmails;

        if (this.sessionService.getUser().stripeAccountId !== null) {
          this.getStripeAccountLink();
        } else {
          this.option.query = `metadata['GolfTechId']:'${this.sessionService.getUserId()}'`;
          this.onboard();
        }

        if (response.operatingHours) {
          const parsedHours = JSON.parse(response.operatingHours) as OperatingHours[];
          // Ensure isNextDay is set for all days
          this.operatingHours = parsedHours.map(day => ({
            ...day,
            isNextDay: day.isNextDay || false
          }));
        } else {
          this.operatingHours = this.days.map(day => ({
            dayOfWeek: day.value,
            openTime: '09:00',
            closeTime: '17:00',
            isOpen: true,
            isNextDay: false
          }));
        }
      },
      error: (error) => {
        console.error(error);
        this.isLoading = false;
      },
      complete: () => this.isLoading = false
    });
  }

  regenerateApiKey(): void {
    let dialogRef = this.dialog.open(AlertModalComponent, {
      width: 'auto',
      height: 'auto',
    });


    dialogRef.componentInstance.title = 'Warning';
    dialogRef.componentInstance.body = 'Are you sure you want to regenerate the API key? This action cannot be undone and will effect the booking functionality on your website. <br /><br />Please re-enter the iFrame with the updated api key on your website to avoid loosing booking functionality.';
    dialogRef.componentInstance.submitText = 'Yes';
    dialogRef.componentInstance.otherText = 'Cancel';
    dialogRef.componentInstance.isOtherTextDisabled = false;

    dialogRef.afterClosed().subscribe(result => {
      if (result.toLowerCase() === 'yes') {
        this.accountService.regenerateApiKey().subscribe({
          next: (response: string) => {

            let dialogRef2 = this.dialog.open(AlertModalComponent, {
              width: 'auto',
              height: 'auto',
            });

            dialogRef2.componentInstance.title = 'New API Key';
            dialogRef2.componentInstance.body = `Do you want to save your new API Key? <br /><br />Your new API Key is: <strong>${response}</strong>`;
            dialogRef2.componentInstance.submitText = 'Yes';
            dialogRef2.componentInstance.otherText = 'No';
            dialogRef2.componentInstance.isOtherTextDisabled = false;

            dialogRef2.afterClosed().subscribe(onSaveResult => {
              if (onSaveResult.toLowerCase() === 'yes') {
                this.organizationForm.get('widgetApiKey').setValue(response);
                this.onOrganizationSubmit();
              }
            });
          },
          error: (error) => {
            this.setError(this.organizationForm.get('widgetApiKey'), 'Unable to regenerate API Key');
            console.error(error);
          },
          complete: () => {
            this.isLoading = false;
          }
        });
      }
    });
  }

  get isFeePercentage(): boolean {
    return this.organizationForm.get('creditCardFeeType').value === CreditCardFeeType.Percentage;
  }

  onFeeTypeChange(): void {
    const feeControl = this.organizationForm.get('creditCardFee');
    if (this.isFeePercentage) {
      feeControl.setValidators([Validators.required, Validators.min(0), Validators.max(100)]);
    } else {
      feeControl.setValidators([Validators.required, Validators.min(0)]);
    }
    feeControl.updateValueAndValidity();
  }

  onUserInfoSubmit(): void {
    if (this.userForm.valid) {
      this.isLoading = true;
      const data = new FormData();
      data.append('id', this.sessionService.getUser().id);
      data.append('firstName', this.userForm.get('firstName').value);
      data.append('lastName', this.userForm.get('lastName').value);
      data.append('email', this.userForm.get('email').value);
      data.append('phoneNumber', this.userForm.get('phoneNumber').value);
      data.append('golfTechType', this.sessionService.getUser().golfTechType.toString());
      data.append('venmoUsername', this.sessionService.getUser().venmoUsername);
      data.append('venmoQrCode', this.sessionService.getUser().venmoQrCode);
      data.append('lastEditedBy', this.sessionService.getUser().id);

      this.accountService.updateUser(this.sessionService.getUser().id, data).subscribe({
        next: (response) => {
          this.loadUserData();
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          this.setError(this.userForm, error);
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
    }
  }

  onOrganizationSubmit(): void {
    if (this.organizationForm.valid) {
      this.isLoading = true;
      const pendingEmail = this.organizationForm.get('additionalEmail').value;

      let data = {
        id: this.sessionService.getUser().organizationId,
        widgetApiKey: this.organizationForm.get('widgetApiKey').value,
        organizationName: this.organizationForm.get('organizationName').value,
        creditCardFee: this.organizationForm.get('creditCardFee').value,
        creditCardFeeType: this.organizationForm.get('creditCardFeeType').value,
        timezone: this.organizationForm.get('timezone').value,
        lastEditedBy: this.sessionService.getUser().id,
        stripeAccountId: this.sessionService.getUser().stripeAccountId,
        additionalEmails: this.additionalEmails,
        operatingHours: JSON.stringify(this.operatingHours)
      };

      this.organizationService.update(data.id, data).subscribe({
        next: (response) => {
          this.additionalEmails = response.additionalEmails;
          this.dataSource.data = this.additionalEmails;
          this.organizationForm.get('additionalEmail').setValue('');
          this.operatingHours = JSON.parse(response.operatingHours) as OperatingHours[];
          this.loadUserData();
          this.isLoading = false;
        },
        error: (error) => {
          console.error(error);
          if (pendingEmail) {
            this.additionalEmails = this.additionalEmails.filter(email => email !== pendingEmail);
            this.dataSource.data = this.additionalEmails;
          }
          this.setError(this.organizationForm, error);
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
          this.operatingHoursErrorMessage = { day: '', message: '' };
        }
      });
    }
  }

  addEmail(): void {
    const email = this.organizationForm.get('additionalEmail').value;
    if (this.organizationForm.get('additionalEmail').valid && email && this.additionalEmails.indexOf(email) === -1) {
      this.additionalEmails.push(email);
      this.onOrganizationSubmit();
      this.emailErrorMessage = '';
    } else if (this.additionalEmails.indexOf(email) !== -1) {
      this.emailErrorMessage = 'This email is already added.';
    }
  }

  removeEmail(email: string): void {
    this.additionalEmails = this.additionalEmails.filter(e => e !== email);
    this.onOrganizationSubmit();
  }

  private generateTimeOptions() {
    for (let hour = 0; hour < 24; hour++) {
      for (let minute of ['00', '30']) {
        const time = `${hour.toString().padStart(2, '0')}:${minute}`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        this.timeOptions.push({ value: time, display: displayTime });
      }
    }

    this.timeOptions = this.timeOptions.filter(t => t.value !== '24:00');
  }

  private generateUsTimezones() {
    // Check if Intl.supportedValuesOf is available (modern browsers)
    if (typeof Intl !== 'undefined' && 'supportedValuesOf' in Intl) {
      try {

        const allTimezones = Intl.supportedValuesOf('timeZone');
        const usTimezones = allTimezones.filter(tz => 
          this.isUsTimezone(tz)
        );
        
        this.usTimezones = usTimezones.map(tz => {
          const now = new Date();
          const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            timeZoneName: 'long'
          });
          
          // Get the timezone name (e.g., "Eastern Standard Time")
          const parts = formatter.formatToParts(now);
          const timeZoneName = parts.find(part => part.type === 'timeZoneName')?.value || tz;
          
          // Get offset info
          const offset = this.getTimezoneOffset(tz, now);
          
          return {
            value: tz,
            label: `${timeZoneName} (${offset}) - ${this.getCityName(tz)}`
          };
        }).sort((a, b) => a.label.localeCompare(b.label));
      } catch (error) {
        console.warn('Intl.supportedValuesOf not supported, using fallback');
        this.generateFallbackTimezones();
      }
    } else {
      // Fallback for older browsers
      this.generateFallbackTimezones();
    }
  }

  private isUsTimezone(timezone: string): boolean {
    // Define specific US timezones to avoid including other American countries
    const usTimezonePatterns = [
      'America/New_York',
      'America/Chicago', 
      'America/Denver',
      'America/Los_Angeles',
      'America/Anchorage',
      'America/Phoenix',
      'America/Detroit',
      'America/Indiana/Indianapolis',
      'America/Indiana/Knox',
      'America/Indiana/Marengo',
      'America/Indiana/Petersburg',
      'America/Indiana/Tell_City',
      'America/Indiana/Vevay',
      'America/Indiana/Vincennes',
      'America/Indiana/Winamac',
      'America/Kentucky/Louisville',
      'America/Kentucky/Monticello',
      'America/Menominee',
      'America/Metlakatla',
      'America/Nome',
      'America/Sitka',
      'America/Yakutat',
      'Pacific/Honolulu'
    ];
    
    return usTimezonePatterns.includes(timezone);
  }

  private generateFallbackTimezones() {
    // Fallback timezone list for older browsers
    this.usTimezones = [
      { value: 'America/New_York', label: 'Eastern Time (ET) - New York, Miami, Atlanta' },
      { value: 'America/Chicago', label: 'Central Time (CT) - Chicago, Dallas, Houston' },
      { value: 'America/Denver', label: 'Mountain Time (MT) - Denver, Phoenix, Salt Lake City' },
      { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles, San Francisco, Seattle' },
      { value: 'America/Anchorage', label: 'Alaska Time (AKT) - Anchorage, Juneau' },
      { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST) - Honolulu' },
      { value: 'America/Phoenix', label: 'Arizona Time (MST) - Phoenix (No DST)' }
    ];
  }

  private getTimezoneOffset(timezone: string, date: Date): string {
    try {     
      const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
      const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
      const offset = (tzDate.getTime() - utcDate.getTime()) / (1000 * 60 * 60);
      
      const sign = offset >= 0 ? ' +' : ' -';
      const hours = Math.abs(Math.floor(offset));
      const minutes = Math.abs((offset % 1) * 60);
      
      return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } catch {
      return '';
    }
  }

  private getCityName(timezone: string): string {
    // Extract city name from timezone identifier dynamically
    // Examples: "America/New_York" -> "New York", "America/Indiana/Indianapolis" -> "Indianapolis"
    const parts = timezone.split('/');
    const cityPart = parts[parts.length - 1]; // Get the last part
    
    // Convert underscores to spaces and capitalize words
    return cityPart
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }


  formatTime(time: string): string {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  validateOperatingHours(day: OperatingHours): boolean {
    if (!day.isOpen) return true;

    // Convert times to minutes for easier comparison
    const [openHour, openMinute] = day.openTime.split(':').map(Number);
    const [closeHour, closeMinute] = day.closeTime.split(':').map(Number);
    
    const openTimeInMinutes = (openHour * 60) + openMinute;
    const closeTimeInMinutes = (closeHour * 60) + closeMinute;

    // If isNextDay is checked, closing time can be earlier than opening time
    if (day.isNextDay) {
      return true;
    }

    // If same day, closing time must be after opening time
    return closeTimeInMinutes > openTimeInMinutes;
  }

  saveOperatingHours(): void {
    this.isLoading = true;
    // Validate all operating hours before saving
    const invalidHours = this.operatingHours.find(day => !this.validateOperatingHours(day));
    if (invalidHours) {
      const dayName = this.days.find(d => d.value === invalidHours.dayOfWeek)?.label;
      this.operatingHoursErrorMessage = { 
        day: dayName, 
        message: 'Closing time must be after opening time or "Is Next Day" must be checked'
      };
      this.isLoading = false;
      return;
    }

    this.onOrganizationSubmit();
  }

  toggleDayOpen(day: OperatingHours): void {
    day.isOpen = !day.isOpen;
    day.isNextDay = false; // Reset isNextDay when toggling day closed
  }

  getStripeAccountLink() {
    this.stripeService.getAccountLoginLink(this.getUserFromSession().stripeAccountId).subscribe({
      next: (result: any) => {
        this.stripeLoginLink = result;
      },
      error: (err) => {
        this.isLoading = false;
        console.log(err);
        // show error message
      },
      complete: () => { this.isLoading = false }
    });
  }

  onboard() {
    this.createConnectExpressAccount();
  }

  private createConnectExpressAccount() {
    const dialogRef = this.dialog.open(StripeConnectExpressCreateModalComponent, {
      width: '35vw',
      disableClose: true
    });

    dialogRef.componentInstance.disableClose = true;
    dialogRef.componentInstance.businessDetailsForm.get('golfTechId').patchValue(this.getUserIdFromSession());
    dialogRef.componentInstance.businessDetailsForm.get('refreshUrl').patchValue(`${environment.dashboardLocation}/my-account/${this.getUserIdFromSession()}`);
    dialogRef.componentInstance.businessDetailsForm.get('returnUrl').patchValue(`${environment.dashboardLocation}/my-account/${this.getUserIdFromSession()}`);
    dialogRef.componentInstance.businessDetailsForm.get('emailAddress').patchValue(this.getUserFromSession().email);
    dialogRef.componentInstance.businessDetailsForm.get('name').patchValue(`${this.getUserFromSession().firstName} ${this.getUserFromSession().lastName}`);

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        this.isLoading = true;
        if (this.getUserFromSession().stripeAccountId === undefined || this.getUserFromSession().stripeAccountId === null) {
          this.stripeService.onboardStripeConnectAccount(result).subscribe((result: any) => {
            if (result) {
              const dialogRef = this.dialog.open(AlertModalComponent, {
                width: '35vw'
              });

              let user = this.sessionService.getUser();
              user.stripeAccountId = result.id;
              this.sessionService.setUser(user);

              dialogRef.componentInstance.title = "Stripe Account Setup";
              dialogRef.componentInstance.body = "Please finish the Stripe Connect Account setup from the email sent to you. You will need to finish this setup inorder to accept payments from customers. Any questions please contact support.";
              dialogRef.componentInstance.submitText = "Okay";

              dialogRef.afterClosed().subscribe(() => {
                this.loadUserData();
                window.location.reload();
                //this.addPaymentMethod();
              });
            }
          }, err => {
            console.log(err);
            this.isLoading = false;
          });
        }
      },
      error: (err) => {
        console.log(err);
        this.isLoading = false;
      }
    });
  }
}
